import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { FiArrowLeft, FiMail, FiMessageCircle } from 'react-icons/fi';
import './SupportPage.css';

type AppInfo = {
  name: string;
  description: string;
  platform: string;
  faqs: { question: string; answer: string }[];
};

const APP_SUPPORT_INFO: Record<string, AppInfo> = {
  'civics-tutor': {
    name: 'Civics Tutor 2025',
    description: 'Study and pass the U.S. Citizenship civics exam with official USCIS questions and interactive study modes.',
    platform: 'iOS (iPhone & iPad)',
    faqs: [
      {
        question: 'How many questions are included in Civics Tutor?',
        answer: 'Civics Tutor includes all 128 official USCIS civics questions that may be asked during your naturalization interview, updated October 2025. The app covers the complete question set used for the U.S. Citizenship test.'
      },
      {
        question: 'What study modes are available?',
        answer: 'The app offers multiple study modes including: Quiz mode for testing your knowledge, study mode to review all questions and answers, multiple choice practice with various difficulty levels, and a progress tracking system to monitor your learning journey.'
      },
      {
        question: 'Does the app work offline?',
        answer: 'Yes! Civics Tutor is designed to work completely offline. All questions, answers, and study materials are included in the app, so you can study anytime, anywhere without an internet connection.'
      },
      {
        question: 'Does Civics Tutor collect any personal data?',
        answer: 'No. Civics Tutor does not collect, transmit, or store any personal data, usage analytics, or telemetry. All study progress is saved locally on your device only. We do not track your activity, and we do not have access to any information about how you use the app. Your privacy is completely protected.'
      },
      {
        question: 'Is my progress saved?',
        answer: 'Yes, your study progress, quiz scores, and achievements are automatically saved locally on your device. Your progress is retained even if you close the app and return later. This data never leaves your device and is not transmitted to our servers.'
      },
      {
        question: 'What language is the app available in?',
        answer: 'Currently, Civics Tutor is available in English. The questions and answers are presented exactly as they appear in the official USCIS materials.'
      },
      {
        question: 'How do I report a bug or issue?',
        answer: 'If you encounter any issues or bugs, please contact our support team at support@codetunezstudios.com with details about the problem, including your device model and iOS version. We typically respond within 24-48 hours.'
      },
      {
        question: 'Can I request a refund?',
        answer: 'All purchases are processed through the Apple App Store. For refund requests, please contact Apple Support directly through your App Store purchase history or visit support.apple.com. Apple handles all refund decisions according to their policies.'
      },
      {
        question: 'Will the questions be updated if the test changes?',
        answer: 'Yes, we monitor official USCIS updates and will release app updates if the civics test questions are modified. Updates are provided free of charge to all users (unless the test changes significantly).'
      }
    ]
  },
  'home-cookbook': {
    name: 'Home Cookbook',
    description: 'A recipe manager and meal planner for organizing recipes, creating shopping lists, and planning meals.',
    platform: 'iOS (iPhone & iPad)',
    faqs: [
      {
        question: 'How do I add a recipe?',
        answer: 'Tap the "+" button on the recipes screen to create a new recipe. You can add ingredients, cooking instructions, images, and more.'
      },
      {
        question: 'Can I share recipes with family and friends?',
        answer: 'Yes! Home Cookbook supports cloud sync, allowing you to share your recipes with others who have the app.'
      },
      {
        question: 'Does the app work without an account?',
        answer: 'Yes, you can use Home Cookbook in local mode without creating an account. Your data will be stored only on your device. Creating an account enables cloud sync and family features.'
      },
      {
        question: 'How do I report a bug or issue?',
        answer: 'If you encounter any issues or bugs, please contact our support team at support@codetunezstudios.com with details about the problem, including your device model and iOS version. We typically respond within 24-48 hours.'
      },
      {
        question: 'Can I request a refund?',
        answer: 'All purchases are processed through the Apple App Store. For refund requests, please contact Apple Support directly through your App Store purchase history or visit support.apple.com. Apple handles all refund decisions according to their policies.'
      }
    ]
  },
  'gridmp3': {
    name: 'GridMP3',
    description: 'A powerful MP3 tag editor built for DJs — bulk-edit metadata, organize large libraries, and keep your collection clean and cue-ready.',
    platform: 'Windows & Mac',
    faqs: [
      {
        question: 'What audio file formats does GridMP3 support?',
        answer: 'GridMP3 primarily focuses on MP3 files and supports ID3v1 and ID3v2 tag formats. The app is optimized for editing metadata in MP3 files commonly used in DJ libraries. GridMP3 also includes a built-in WAV to MP3 converter for easy file format conversion.'
      },
      {
        question: 'Can I edit multiple files at once?',
        answer: 'Yes! GridMP3 is designed for bulk editing. You can select multiple files and update tags like artist, album, genre, or any other metadata across all selected files simultaneously. This is perfect for organizing large music libraries efficiently.'
      },
      {
        question: 'Does GridMP3 detect BPM and musical key?',
        answer: 'Yes, GridMP3 includes built-in audio analysis to detect BPM (beats per minute) and musical key for your tracks. This information is automatically written to the appropriate ID3 tags, making it easy to sort and filter your library by tempo and key.'
      },
      {
        question: 'Can I preview audio files before editing?',
        answer: 'Absolutely! GridMP3 includes a built-in audio player that lets you preview tracks before making any changes. You can listen to your music while editing tags to ensure you\'re making the right updates.'
      },
      {
        question: 'How does the find and replace feature work?',
        answer: 'The find and replace tool allows you to search for specific text within any tag field and replace it across multiple files. For example, you can fix typos in artist names, standardize genre tags, or update label information across your entire collection in seconds.'
      },
      {
        question: 'Will GridMP3 modify my original audio files?',
        answer: 'GridMP3 only modifies the metadata (ID3 tags) embedded in your MP3 files. The actual audio data remains completely unchanged. Your sound quality and waveforms are never altered.'
      },
      {
        question: 'Can I filter and search my music library?',
        answer: 'Yes, GridMP3 provides powerful filtering capabilities. You can filter your library by any tag field, BPM range, key, or custom criteria to quickly find exactly the tracks you need. This is especially useful for large DJ collections.'
      },
      {
        question: 'Is there a way to undo changes?',
        answer: 'Yes, GridMP3 includes an undo feature that allows you to revert recent tag edits. However, we recommend backing up important files before making bulk changes to large libraries.'
      },
      {
        question: 'What are the system requirements?',
        answer: 'GridMP3 is available for Windows 10/11 and macOS 11 (Big Sur) or later. The app requires minimal system resources and works smoothly even with libraries containing thousands of tracks.'
      },
      {
        question: 'How do I report a bug or issue?',
        answer: 'If you encounter any issues or bugs, please contact our support team at support@codetunezstudios.com with details about the problem, including your device model and operating system version. We typically respond within 24-48 hours.'
      },
      {
        question: 'Can I request a refund?',
        answer: 'For Windows: Refund requests are handled through the Microsoft Store. For Mac: Refund requests are handled through the Apple App Store. Please contact the respective store\'s support team directly for refund inquiries.'
      }
    ]
  },
  'locknkey': {
    name: 'Lock-n-Key',
    description: 'A password manager for KeePass databases — securely store, organize, and access your credentials across Windows, Mac, and iPhone.',
    platform: 'Windows, Mac & iOS',
    faqs: [
      {
        question: 'What is KeePass and why does Lock-n-Key use it?',
        answer: 'KeePass is an open-source password database format (.kdbx) that is widely trusted and audited by security experts. Lock-n-Key uses this format to ensure your passwords are stored in a secure, encrypted database that you fully control. You can also use your database with other KeePass-compatible applications.'
      },
      {
        question: 'Where are my passwords stored?',
        answer: 'Your password database is stored locally on your device and is encrypted with AES-256 encryption. Lock-n-Key never stores your passwords on our servers. You have complete control over your database file and can sync it yourself using cloud storage services like iCloud, Dropbox, or OneDrive if desired.'
      },
      {
        question: 'Can I sync my passwords across multiple devices?',
        answer: 'Yes! You can store your KeePass database file in a cloud storage service (iCloud, Dropbox, Google Drive, OneDrive, etc.) and open it from Lock-n-Key on any of your devices. Changes are saved to the database file, which syncs through your chosen cloud provider.'
      },
      {
        question: 'How do I import passwords from another password manager?',
        answer: 'Most password managers allow you to export your data to CSV or other formats. You can then import this data into Lock-n-Key, or use KeePass desktop tools to convert the data to .kdbx format. Detailed import guides are available in our documentation.'
      },
      {
        question: 'Is my data encrypted?',
        answer: 'Yes, absolutely. Your entire password database is encrypted using AES-256 encryption with your master password. Lock-n-Key never has access to your master password or your encrypted data. Only you can unlock your database.'
      },
      {
        question: 'What happens if I forget my master password?',
        answer: 'Unfortunately, if you forget your master password, there is no way to recover your database. This is by design — the encryption is so strong that even we cannot help you recover it. We strongly recommend keeping a secure backup of your master password in a safe location.'
      },
      {
        question: 'Can I use Lock-n-Key offline?',
        answer: 'Yes! Lock-n-Key is designed for being offline. Your database is stored locally on your device, and you can access all your passwords without an internet connection.'
      },
      {
        question: 'Does Lock-n-Key connect to any servers?',
        answer: 'No. Lock-n-Key does not connect to any servers or transmit your data over the internet. The only time internet is used is if you optionally request to fetch a website\'s icon/favicon for visual display in your password entries. All password data stays completely local on your device.'
      },
      {
        question: 'Does Lock-n-Key have a password generator?',
        answer: 'Yes, Lock-n-Key includes a built-in password generator that creates strong, random passwords with customizable length and character requirements (uppercase, lowercase, numbers, symbols).'
      },
      {
        question: 'How do I report a security issue?',
        answer: 'Security is our top priority. If you discover a security vulnerability, please email us immediately at support@codetunezstudios.com with "SECURITY" in the subject line. We take all reports seriously and will respond promptly.'
      },
      {
        question: 'How do I report a bug or issue?',
        answer: 'If you encounter any issues or bugs, please contact our support team at support@codetunezstudios.com with details about the problem, including your device model and operating system version. We typically respond within 24-48 hours.'
      },
      {
        question: 'Can I request a refund?',
        answer: 'For Windows: Refund requests are handled through the Microsoft Store. For Mac: Refund requests are handled through the Apple App Store. For iOS: Refund requests are handled through the Apple App Store. Please contact the respective store\'s support team directly for refund inquiries.'
      }
    ]
  },
  'footy-creator': {
    name: 'Footy Creator',
    description: 'A specialized content portal for online creators built around any soccer team in the world — stats, media tools, and community features in one place.',
    platform: 'Web Application',
    faqs: [
      {
        question: 'What is Footy Creator?',
        answer: 'Footy Creator is a web-based platform designed for soccer content creators, podcasters, YouTubers, and social media influencers. It provides live match data, player statistics, formation builders, prediction tools, and community features all focused on your favorite soccer team.'
      },
      {
        question: 'Which teams and leagues are supported?',
        answer: 'Footy Creator supports teams from major soccer leagues around the world, including the Premier League, La Liga, Serie A, Bundesliga, Ligue 1, MLS, and many others. You can select your team during setup and access comprehensive data for that team.'
      },
      {
        question: 'How do I create match predictions?',
        answer: 'Navigate to the Predictions section, select an upcoming match, and submit your score prediction. You can compete with friends and other fans to see who has the most accurate predictions over the season.'
      },
      {
        question: 'Can I create and share formations?',
        answer: 'Yes! The Formation Builder tool lets you create custom formations, position players, and save or share your tactical setups. This is perfect for creating content discussing team strategies and lineup predictions.'
      },
      {
        question: 'How do player ratings work?',
        answer: 'After each match, you can rate players on a scale (typically 1-10). Your ratings are saved and you can view historical ratings to track player performance over time. This is great for podcasts and video content discussing player form.'
      },
      {
        question: 'Are there community features?',
        answer: 'No not yet, but we\'re planning to add features like fan forums, content sharing, and collaborative prediction leagues in future updates based on user feedback. Stay tuned for the 2026/2027 season!'
      },
      {
        question: 'Is Footy Creator free to use?',
        answer: 'For now, Footy Creator is invite-only, but a pricing model will be launched for the 2026/2027 season. The good news is the platform is ready to support thousands of content creators, so we\'re excited to open up access soon!'
      },
      {
        question: 'Can I use Footy Creator data in my videos or podcasts?',
        answer: 'Yes! Footy Creator is designed for content creators. You can use the data, formations, and statistics in your content. However, please give appropriate attribution and review our terms of service for specific usage guidelines.'
      },
      {
        question: 'How often is match data updated?',
        answer: 'Footy Creator is powered by the same data providers as major sports networks. Live match data is updated in real-time during games (~1 minute delay). Statistics, standings, and other data are updated regularly, typically within minutes after official sources publish updates.'
      },
      {
        question: 'How do I report a bug or issue?',
        answer: 'If you encounter any issues or bugs, please contact our support team at support@codetunezstudios.com with details about the problem, including your browser and operating system. We typically respond within 24-48 hours.'
      },
      {
        question: 'Can I request a refund?',
        answer: 'Refund requests for premium subscriptions are handled on a case-by-case basis. Please contact our support team at support@codetunezstudios.com with your account details and reason for the refund request.'
      }
    ]
  },
  'token-kit': {
    name: 'token-kit',
    description: 'An AI platform that lets small app developers integrate LLMs into their products — powered by tokens purchased directly by the end consumer.',
    platform: 'Developer Platform / API',
    faqs: [
      {
        question: 'What is token-kit?',
        answer: 'token-kit is a platform that allows app developers to integrate AI language models (like GPT, Claude, etc.) into their applications without managing API keys or billing. End users purchase tokens directly through our AI Tokens platform and use them across any token-kit powered app.'
      },
      {
        question: 'How is token-kit different from using OpenAI or Anthropic APIs directly?',
        answer: 'With token-kit, you don\'t need to manage API keys, handle billing, or worry about rate limits. Your users buy tokens once and can use them across all token-kit apps. This eliminates the barrier of API key management for non-technical users and reduces your billing overhead.'
      },
      {
        question: 'How do I integrate token-kit into my app?',
        answer: 'Integration is straightforward using our SDKs (available for JavaScript/NodeJS) Documentation and code samples are available at token-kit.com. You\'ll need to register your app on the platform and implement the token-kit authentication flow.'
      },
      {
        question: 'What AI models are available through token-kit?',
        answer: 'token-kit provides access to leading AI models including GPT-4, GPT-3.5, Claude, and others. The specific models available may vary, and we regularly add support for new models as they become available.'
      },
      {
        question: 'How does pricing work for developers?',
        answer: 'Developers pay a simple flat fee based on usage tiers depending on the number of requests they need to make. The cost of using LLMs is funded by the end user through token purchases, which keeps developer costs predictable and straightforward. There are no complicated revenue sharing agreements or surprise bills.'
      },
      {
        question: 'How do users purchase tokens?',
        answer: 'Users purchase tokens through our AI Tokens platform (ai-tokens.me). They can buy token packages that work across all token-kit enabled applications. This creates a unified token economy that benefits both developers and users.'
      },
      {
        question: 'Are there usage limits or rate limits?',
        answer: 'Rate limits are based on the user\'s token balance and the underlying AI model\'s capabilities. We implement fair usage policies to prevent abuse while ensuring legitimate users have a smooth experience.'
      },
      {
        question: 'Can I use token-kit for commercial applications?',
        answer: 'Yes! token-kit is designed for commercial use. Many developers use token-kit to monetize their AI-powered features without worrying about the complexity of billing users for API usage.'
      },
      {
        question: 'Where can I find documentation and support?',
        answer: 'Complete documentation is available at token-kit.com. For technical support, email support@codetunezstudios.com. We also have a developer community on Discord where you can ask questions and share experiences.'
      },
      {
        question: 'How do I report a bug or issue?',
        answer: 'If you encounter any issues or bugs, please contact our support team at support@codetunezstudios.com with details about the problem, including your development environment and integration details. We typically respond within 24-48 hours.'
      },
      {
        question: 'Can I request a refund?',
        answer: 'token-kit is a free platform for developers. If you have concerns about billing or revenue sharing, please contact our support team at support@codetunezstudios.com to discuss your specific situation.'
      }
    ]
  },
  'ai-tokens': {
    name: 'AI Tokens',
    description: 'A place to buy and manage tokens for apps powered by token-kit. Purchase tokens directly and use them across any app in the token-kit ecosystem.',
    platform: 'Web Application',
    faqs: [
      {
        question: 'What are AI Tokens?',
        answer: 'AI Tokens are credits that you can use to access AI-powered features in apps that use the token-kit platform. Instead of each app requiring its own subscription or API keys, you buy tokens once and use them across all token-kit enabled applications.'
      },
      {
        question: 'How do I purchase tokens?',
        answer: 'Visit ai-tokens.me and create an account. You can purchase token packages using credit card, PayPal, or other supported payment methods. Tokens are immediately added to your account after purchase.'
      },
      {
        question: 'Which apps can I use my tokens in?',
        answer: 'Your tokens work in any application that is powered by token-kit. You can see a list of compatible apps on ai-tokens.me. As more developers integrate token-kit, the ecosystem of apps continues to grow.'
      },
      {
        question: 'Do tokens expire?',
        answer: 'No, tokens do not expire. Once purchased, they remain in your account until you use them. You can use them at your own pace without worrying about expiration dates.'
      },
      {
        question: 'How much do tokens cost?',
        answer: 'Token packages are available at various price points to suit different usage levels. Larger packages typically offer better value per token. Current pricing is available on ai-tokens.me. Prices may vary based on the underlying AI model costs.'
      },
      {
        question: 'How many tokens do I need?',
        answer: 'Token usage varies depending on the app and the AI model being used. Generally, complex or longer AI interactions use more tokens. Most apps display estimated token costs before you use AI features, so you can make informed decisions.'
      },
      {
        question: 'Can I get a refund on unused tokens?',
        answer: 'Refund policies vary based on your region and local regulations. Generally, unused tokens are non-refundable, but if you experience technical issues or have concerns, please contact support@codetunezstudios.com and we\'ll work with you to find a solution.'
      },
      {
        question: 'Is my payment information secure?',
        answer: 'Yes, we use industry-standard payment processors and do not store your payment card details on our servers. All transactions are encrypted and processed through PCI-compliant payment gateways.'
      },
      {
        question: 'Can I track my token usage?',
        answer: 'Yes! Your AI Tokens account includes a usage dashboard where you can see your token balance, transaction history, and usage across different apps. This helps you monitor your spending and token consumption.'
      },
      {
        question: 'Are there subscription plans?',
        answer: 'Currently, AI Tokens operates on a pay-as-you-go model with no subscriptions. We believe this gives users more control and flexibility. However, we may introduce subscription options in the future based on user feedback.'
      },
      {
        question: 'How do I report a bug or issue?',
        answer: 'If you encounter any issues or bugs, please contact our support team at support@codetunezstudios.com with details about the problem, including your browser and operating system. We typically respond within 24-48 hours.'
      },
      {
        question: 'Can I request a refund?',
        answer: 'Refund policies vary based on your region and local regulations. Generally, unused tokens are non-refundable, but if you experience technical issues or have concerns, please contact support@codetunezstudios.com and we\'ll work with you to find a solution.'
      }
    ]
  },
  'token-kit-sdk': {
    name: 'token-kit SDK',
    description: 'Open-source SDK for integrating token-kit into your JavaScript/TypeScript applications. Build AI-powered features with simple, developer-friendly APIs.',
    platform: 'NPM Package',
    faqs: [
      {
        question: 'How do I install the token-kit SDK?',
        answer: 'You can install the SDK using npm or yarn: `npm install @codetunezstudios/token-kit` or `yarn add @codetunezstudios/token-kit`. The package works in Node.js, React, React Native, and other JavaScript/TypeScript environments.'
      },
      {
        question: 'What are the system requirements?',
        answer: 'The token-kit SDK requires Node.js 14 or higher (or equivalent runtime environment) and supports TypeScript 4.0+. It works in browser environments, Node.js servers, and React Native mobile apps.'
      },
      {
        question: 'Where can I find documentation?',
        answer: 'Complete documentation is available on the NPM package page at https://www.npmjs.com/package/@codetunezstudios/token-kit and on our website at token-kit.com. The SDK includes TypeScript type definitions for better developer experience.'
      },
      {
        question: 'Is the SDK open source?',
        answer: 'Yes! The token-kit SDK is open source and available on GitHub. You can view the source code, contribute to the project, and report issues through our GitHub repository. Check the NPM package page for the repository link.'
      },
      {
        question: 'How do I get started with the SDK?',
        answer: 'After installation, import the SDK and initialize it with your API key from token-kit.com. The documentation includes quickstart guides, code examples, and sample projects to help you integrate AI features into your application quickly.'
      },
      {
        question: 'Does the SDK support TypeScript?',
        answer: 'Yes! The SDK is written in TypeScript and includes full type definitions. You get autocomplete, type checking, and inline documentation in your IDE when using the package.'
      },
      {
        question: 'What features does the SDK provide?',
        answer: 'The SDK provides easy access to token-kit\'s AI models, token management, user authentication, and usage tracking. It handles API calls, error handling, and response parsing automatically, so you can focus on building features.'
      },
      {
        question: 'Can I use the SDK in React Native apps?',
        answer: 'Yes, the token-kit SDK is designed to work in React Native applications. It handles platform-specific differences automatically and provides the same API across web and mobile environments.'
      },
      {
        question: 'How do I report bugs or request features for the SDK?',
        answer: 'Please report bugs or feature requests through GitHub issues on our repository, or email support@codetunezstudios.com. We actively maintain the SDK and regularly release updates based on community feedback.'
      },
      {
        question: 'Is there a community for SDK users?',
        answer: 'Yes! Join our developer community on Discord where you can ask questions, share projects, and get help from other developers using the token-kit SDK. The invite link is available on token-kit.com.'
      }
    ]
  }
};

const SupportPage: React.FC = () => {
  const { appSlug } = useParams<{ appSlug: string }>();
  const appInfo = appSlug ? APP_SUPPORT_INFO[appSlug] : null;

  if (!appInfo) {
    return (
      <main className="support-page">
        <div className="support-page__hero">
          <div className="container">
            <Link to="/" className="support-page__back">
              <FiArrowLeft /> Back to Home
            </Link>
            <h1 className="support-page__title">App Not Found</h1>
            <p className="support-page__subtitle">
              The support page you're looking for doesn't exist.
            </p>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="support-page">
      <div className="support-page__hero">
        <div className="container">
          <Link to="/" className="support-page__back">
            <FiArrowLeft /> Back to Home
          </Link>
          <h1 className="support-page__title">{appInfo.name} Support</h1>
          <p className="support-page__subtitle">
            {appInfo.description}
          </p>
          <div className="support-page__platform">
            <span className="support-page__platform-badge">{appInfo.platform}</span>
          </div>
        </div>
      </div>

      <div className="container support-page__body">
        <article className="support-page__content">
          <section className="support-page__faq-section">
            <h2>Frequently Asked Questions</h2>
            <div className="support-page__faq-list">
              {appInfo.faqs.map((faq, index) => (
                <div key={index} className="support-page__faq-item">
                  <h3 className="support-page__faq-question">{faq.question}</h3>
                  <p className="support-page__faq-answer">{faq.answer}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="support-page__additional-info">
            <h2>Additional Resources</h2>
            <ul>
              <li>
                <Link to="/privacy">Privacy Policy</Link> - Learn how we protect your data
              </li>
            </ul>
          </section>

          <section className="support-page__contact-section">
            <h2>Get Help</h2>
            <p>
              Need assistance? We're here to help! Choose one of the options below to get support.
            </p>

            <div className="support-page__contact-cards">
              <div className="support-page__contact-card">
                <div className="support-page__contact-icon">
                  <FiMail size={24} />
                </div>
                <h3>Email Support</h3>
                <p>Send us an email and we'll respond within 24-72 hours.</p>
                <a href="mailto:support@codetunezstudios.com" className="support-page__contact-link">
                  support@codetunezstudios.com
                </a>
              </div>

              <div className="support-page__contact-card">
                <div className="support-page__contact-icon">
                  <FiMessageCircle size={24} />
                </div>
                <h3>General Contact</h3>
                <p>For general inquiries or feedback about our apps.</p>
                <a href="mailto:contact@codetunezstudios.com" className="support-page__contact-link">
                  contact@codetunezstudios.com
                </a>
              </div>
            </div>
          </section>

          <section className="support-page__company-info">
            <h2>About CodeTunez Studios</h2>
            <p>
              <strong>CodeTunez Studios LLC</strong> develops high-quality applications for iOS, Windows, and Mac.
              We're committed to creating thoughtfully designed software that helps people accomplish their goals.
            </p>
            <p>
              For more information about our company and other products, visit our{' '}
              <Link to="/">homepage</Link>.
            </p>
          </section>
        </article>
      </div>
    </main>
  );
};

export default SupportPage;
